import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  Grid2 as Grid,
  Fade,
  Button,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { pipeline_tree } from "../../../data/city_sites/permitTypesTree";
import ConfirmSelectedAddress from "./ConfirmSelectedAddress";
import { handlePreSaveCitySelections } from "../../../store/actions/uiActions/myPermitsActions";
import { getPreselectedSteps } from "./getPreselectedSteps";
import { structureTypesMapping } from "../../../data/city_sites/structureTypesMapping";
import {
  setAutofillWorkType,
  setAutofillWorkTypeID,
} from "../../../store/actions/dataActions";
import { useFormikContext } from "formik";

const getOptions = (stepKey, miniMap) => {
  try {
    const permit = pipeline_tree.find((p) => p.id === miniMap[0]);
    if (!permit) return [];

    if (stepKey === "ProposedUseId") {
      return permit.proposed_use || [];
    }

    const proposed = (permit.proposed_use || []).find(
      (p) => p.id === miniMap[1]
    );
    if (!proposed) return [];

    if (stepKey === "StructureTypeId") {
      return proposed.structure_type || [];
    }

    const structure = (proposed.structure_type || []).find(
      (s) => s.id === miniMap[2]
    );
    if (!structure) return [];

    if (stepKey === "WorkTypeId") {
      return structure.work_type || [];
    }

    const work = (structure.work_type || []).find((w) => w.id === miniMap[3]);
    if (!work) return [];

    if (stepKey === "WorkSubTypeId") {
      return work.work_sub_type || [];
    }

    return [];
  } catch (err) {
    console.error("getOptions error", err);
    return [];
  }
};

const formatLabel = (str) => {
  return str
    .replace(/_/g, " ") // convert underscores to spaces
    .replace(/(^|[/\s])([a-zA-Z])/g, (match, p1, p2) => p1 + p2.toUpperCase());
};

const RenderCategoryBinds = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);
  const { autofillWorkType, autofillWorkTypeID } = useSelector(
    (state) => state.nocData.quickActions
  );

  const formik = useFormikContext();

  const initialPermitTypeId =
    selectedRow?.connect_to_city?.permit_type_dict?.id;
  const { cityPermitLoading } = useSelector(
    (state) => state.myPermitsComponent.citySite
  );
  const p_u_c = selectedRow?.realEstatePresets?.p_u_c;
  const PermitTypeId = initialPermitTypeId;
  const { result: initialSelections } = getPreselectedSteps(
    p_u_c,
    structureTypesMapping,
    PermitTypeId,
    getOptions
  );

  const [miniMap, setMinimap] = useState(initialSelections.miniMap);
  const [formSelections, setFormSelections] = useState(
    initialSelections.formSelections
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const dispatch = useDispatch();

  const autoFillWorkTypeRef = useRef();

  const steps = useMemo(
    () => [
      { key: "ProposedUseId", label: "Proposed Use" },
      { key: "StructureTypeId", label: "Structure Type" },
      { key: "WorkTypeId", label: "Work Type" },
      { key: "WorkSubTypeId", label: "Work Sub Type" },
    ],
    []
  );

  const setWorkType = useCallback(
    (workTypeId) => {
      if (!workTypeId) {
        // clear formik label if needed
        formik?.setFieldValue("general", "");
        setFormSelections((prev) => ({ ...prev, WorkTypeId: undefined }));
        // also prune minimap tail
        setMinimap(([permitId, pu, st]) => [permitId, pu, st]);
        return;
      }

      // Build the base (permit, proposed, structure) to fetch options
      const baseMiniMap = [
        initialPermitTypeId,
        formSelections.ProposedUseId,
        formSelections.StructureTypeId,
      ];

      const options = getOptions("WorkTypeId", baseMiniMap);
      const found = options.find((opt) => opt.id === workTypeId);

      // Your UI shows label from option.name (formatted). Use same here.
      const label = found ? formatLabel(found.name) : "";

      // Update local state
      setFormSelections((prev) => ({ ...prev, WorkTypeId: workTypeId }));
      setMinimap([...baseMiniMap, workTypeId]);

      // Update Formik (dot paths are supported)
      formik?.setFieldValue("general", label);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formSelections.ProposedUseId,
      formSelections.StructureTypeId,
      initialPermitTypeId,
    ]
  );

  useEffect(() => {
    if (!currentUser?.user?.selectedCountiesNOC || !selectedRow?.county) return;

    const matchedCounty = currentUser.user.selectedCountiesNOC.find((item) => {
      const countyCleaned = item?.name?.toLowerCase().replace(/\s+/g, "_");
      return (
        countyCleaned ===
        selectedRow?.county?.toLowerCase().replace(/\s+/g, "_")
      );
    });

    const proposedUseValid = formSelections.ProposedUseId;
    const structureTypeValid = formSelections.StructureTypeId;

    if (proposedUseValid && structureTypeValid) {
      const testMiniMap = [
        initialPermitTypeId,
        proposedUseValid,
        structureTypeValid,
      ];
      const validWorkTypes = getOptions("WorkTypeId", testMiniMap);

      const isWorkTypeValid = validWorkTypes.some(
        (opt) => opt.id === matchedCounty?.defaults?.workType?.city_id
      );

      if (isWorkTypeValid && !autofillWorkTypeID) {
        setWorkType(matchedCounty.defaults.workType.city_id);
        // setFormSelections((prev) => ({
        //   ...prev,
        //   WorkTypeId: matchedCounty.defaults.workType.city_id,
        // }));

        // setMinimap((prev) => [
        //   ...testMiniMap,
        //   matchedCounty.defaults.workType.city_id,
        // ]);
      } else if (autofillWorkTypeID) {
        setWorkType(autofillWorkTypeID);
        // setFormSelections((prev) => ({
        //   ...prev,
        //   WorkTypeId: autofillWorkTypeID,
        // }));

        // setMinimap((prev) => [...testMiniMap, autofillWorkTypeID]);
      }
    }
  }, [
    setWorkType,
    autofillWorkTypeID,
    currentUser.user.selectedCountiesNOC,
    formSelections.ProposedUseId,
    formSelections.StructureTypeId,
    initialPermitTypeId,
    selectedRow?.county,
  ]);

  // useEffect(() => {
  //   const handleFillWorkType = async () => {
  //     const testMiniMap = [
  //       initialPermitTypeId,
  //       formSelections.ProposedUseId,
  //       formSelections.StructureTypeId,
  //     ];
  //     const options = getOptions("WorkTypeId", testMiniMap);

  //     const match = options.find(
  //       (opt) =>
  //         opt.name?.toLowerCase().includes("window") &&
  //         opt.name?.toLowerCase().includes("door")
  //     );

  //     if (match) {
  //       await dispatch(setAutofillWorkTypeID(match.id));
  //       await dispatch(setAutofillWorkType(false));
  //     }
  //   };

  //   if (
  //     autofillWorkType &&
  //     formSelections.ProposedUseId &&
  //     formSelections.StructureTypeId &&
  //     !autoFillWorkTypeRef?.current
  //   ) {
  //     autoFillWorkTypeRef.current = true;
  //     handleFillWorkType();
  //   }
  // }, [
  //   autofillWorkType,
  //   formSelections.ProposedUseId,
  //   formSelections.StructureTypeId,
  //   initialPermitTypeId,
  //   dispatch,
  // ]);

  const preSaveHandler = () => {
    dispatch(handlePreSaveCitySelections({ miniMap, formSelections }));
  };

  const handleChange = (step, value) => {
    const stepIndex = steps.findIndex((s) => s.key === step);
    const newFormSelections = { ...formSelections };

    // Set current step value, clear all later steps
    steps.forEach(({ key }, i) => {
      if (i < stepIndex) return;
      newFormSelections[key] = i === stepIndex ? value : undefined;
    });

    // Recalculate miniMap based on valid steps
    const newMiniMap = [initialPermitTypeId];

    steps.slice(0, stepIndex + 1).forEach(({ key }) => {
      const val = newFormSelections[key];
      if (val !== undefined) {
        newMiniMap.push(...(Array.isArray(val) ? val : [val]));
      }
    });
    // ✅ Prune future steps if their options are no longer valid
    steps.slice(stepIndex + 1).forEach(({ key }) => {
      const nextOptions = getOptions(key, newMiniMap, newFormSelections);
      const currentVal = newFormSelections[key];

      if (
        !nextOptions.some((opt) => {
          return Array.isArray(currentVal)
            ? currentVal.includes(opt.id)
            : opt.id === currentVal;
        })
      ) {
        newFormSelections[key] = undefined;
      }
    });

    if (step === "WorkTypeId") {
      const options = getOptions("WorkTypeId", miniMap);
      const found = options.find((opt) => opt.id === value);

      const label = found ? formatLabel(found.name) : "";

      if (label) {
        formik?.setFieldValue("general", label);
      }
    }

    setFormSelections(newFormSelections);
    setMinimap(newMiniMap);
  };

  const allStepsCompleted = steps.every(({ key }, index) => {
    const options = getOptions(key, miniMap, formSelections);
    if (!options.length) return true; // step not needed
    const val = formSelections[key];
    return (
      val !== undefined &&
      val !== "" &&
      (Array.isArray(val) ? val.length > 0 : true)
    );
  });

  const enableButton = useMemo(() => allStepsCompleted, [allStepsCompleted]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          mt: 1.5,
        }}
      >
        <Typography variant="h5" align="center">
          Creating {selectedRow?.connect_to_city?.permit_type_dict?.label}
        </Typography>
        <Typography variant="body2" fontWeight="bold" align="center">
          Please complete the following specs so we can connect to the
          Jacksonville city permitting system (JaxEPICS) and automatically start
          your permit package, including generating your permit number.
        </Typography>
      </Box>
      <Grid container spacing={1} mt={2}>
        {steps.map((stepObj, index) => {
          const prevStepFilled =
            index === 0 || formSelections[steps[index - 1].key] !== undefined;
          const isDisabled = !prevStepFilled;
          const step = stepObj.key;
          const label = stepObj.label;

          const options = getOptions(step, miniMap);
          if (!options.length) return null;

          const isMulti = step === "WorkSubTypeId"; // explicitly define it
          const value = formSelections[step] || (isMulti ? [] : "");

          return (
            <Grid size={{ sm: 12, md: 6 }} key={step}>
              <FormControl variant="filled" fullWidth disabled={isDisabled}>
                <InputLabel id={`${step}-label`}>{label}</InputLabel>
                <Select
                  labelId={`${step}-label`}
                  multiple={isMulti}
                  value={value}
                  onChange={(e) => handleChange(step, e.target.value)}
                  renderValue={(selected) => {
                    const selectedArray = isMulti ? selected : [selected];
                    const labels = selectedArray
                      .map((val) => {
                        const found = options.find((opt) => opt.id === val);
                        return formatLabel(found?.name || val);
                      })
                      .filter(Boolean);
                    return isMulti ? labels.join(", ") : labels[0];
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        marginTop: "1.5px",
                        maxHeight: 190, // Set the desired max height for the dropdown menu
                        width: "auto",
                        maxWidth: "100%",
                      },
                    },
                  }}
                >
                  {options.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {isMulti && (
                        <Checkbox checked={value.includes(option.id)} />
                      )}
                      <ListItemText primary={formatLabel(option.name)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          );
        })}
      </Grid>
      <Fade in={enableButton} timeout={{ enter: 600, exit: 200 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 1,
            opacity: enableButton ? 1 : 0,
            transform: enableButton ? "translateY(0)" : "translateY(10px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <Button
            disabled={cityPermitLoading}
            size="small"
            sx={{ minWidth: "10rem" }}
            variant="contained"
            color="primary"
            onClick={() => {
              preSaveHandler();
              setConfirmOpen(true);
            }}
          >
            Select & Continue
          </Button>
        </Box>
      </Fade>
      <ConfirmSelectedAddress
        confirmOpen={confirmOpen}
        setConfirmOpen={setConfirmOpen}
        msg="Once you continue, your permit number will be created and the permit process will be initiated. You won’t be able to modify your selections afterward."
        step={2}
        // formikValues={formikValues}
      />
    </Box>
  );
};

export default RenderCategoryBinds;
